import React, { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { getCsrfToken } from '@/shared/utils/csrf';
import { requestMagicLink } from '../api';
import { isValidEmail } from '../utils';
import MagicLinkSent from './MagicLinkSent';
import TurnstileWidget from './TurnstileWidget';

export interface TwoStepSignInProps {
  initialEmail?: string | null;
  emailLocked?: boolean;
  purpose?: 'web' | 'invitation';
  returnPath?: string | null;
  invitationRedirectPath?: string | null;
  showRememberMe?: boolean;
  forgotPasswordUrl?: string;
  sessionPath?: string;
  fallbackId?: string | null;
  captchaSiteKey?: string | null;
  csrfToken?: string;
}

type Step = 'email' | 'password';

const EMAIL_ERROR = 'Enter a valid email address.';
const CAPTCHA_ERROR = 'Please confirm you are not a robot, then try again.';
const NETWORK_ERROR = "We couldn't send the link. Please try again.";

// The server renders the classic form first so sign-in works without JS.
// Read what the person may already have typed or autofilled before replacing it.
const readFallback = (fallbackId: string | null) => {
  if (!fallbackId || typeof document === 'undefined') return { email: '', password: '' };
  const root = document.getElementById(fallbackId);
  return {
    email: root?.querySelector<HTMLInputElement>('input[name="user[email]"]')?.value || '',
    password: root?.querySelector<HTMLInputElement>('input[name="user[password]"]')?.value || '',
  };
};

// Email first, then the password step slides open with two buttons: the
// classic Devise password post and an emailed sign-in link. The password
// field stays in the DOM while closed (max-height 0, not display:none) so
// password managers can still fill it.
const TwoStepSignIn: React.FC<TwoStepSignInProps> = ({
  initialEmail = null,
  emailLocked = false,
  purpose = 'web',
  returnPath = null,
  invitationRedirectPath = null,
  showRememberMe = false,
  forgotPasswordUrl = '/users/password/new',
  sessionPath = '/users/sign_in',
  fallbackId = null,
  captchaSiteKey = null,
  csrfToken,
}) => {
  const [fallback] = useState(() => readFallback(fallbackId));
  const startEmail = fallback.email || initialEmail || '';
  const [email, setEmail] = useState(startEmail);
  const [password, setPassword] = useState(fallback.password);
  const [step, setStep] = useState<Step>(emailLocked || isValidEmail(startEmail) ? 'password' : 'email');
  const [emailError, setEmailError] = useState<string | null>(null);
  const [magicState, setMagicState] = useState<'idle' | 'sending' | 'sent'>('idle');
  const [magicError, setMagicError] = useState<string | null>(null);
  const [captchaToken, setCaptchaToken] = useState<string | null>(null);
  const [captchaResetKey, setCaptchaResetKey] = useState(0);
  const passwordRef = useRef<HTMLInputElement>(null);
  const emailRef = useRef<HTMLInputElement>(null);
  const csrf = csrfToken || getCsrfToken();
  const open = step === 'password';
  const hiddenTab = open ? undefined : -1;

  useLayoutEffect(() => {
    if (fallbackId) document.getElementById(fallbackId)?.remove();
  }, [fallbackId]);

  useEffect(() => {
    if (open) {
      passwordRef.current?.focus();
    } else {
      emailRef.current?.focus();
    }
  }, [open]);

  // The Turnstile widget only renders while the password step is open; once it
  // unmounts (e.g. "Change" back to the email step) its token is stale and
  // must not re-enable the magic-link button on a later, unwidgeted render.
  useEffect(() => {
    if (!open) setCaptchaToken(null);
  }, [open]);

  const revealPassword = () => {
    if (!isValidEmail(email)) {
      setEmailError(EMAIL_ERROR);
      return;
    }
    setEmailError(null);
    setStep('password');
  };

  // "Change" link on the password step: back to step 1, email editable again.
  const changeEmail = () => {
    setStep('email');
  };

  const handleSubmit = (event: React.FormEvent<HTMLFormElement>) => {
    if (step === 'email') {
      event.preventDefault();
      revealPassword();
    }
    // On the password step the browser posts the form to Devise as before.
  };

  const handlePasswordChange = (value: string) => {
    setPassword(value);
    if (value && step === 'email' && isValidEmail(email)) setStep('password');
  };

  // Chrome fills saved passwords without firing input events until the person
  // interacts; the :-webkit-autofill animation (dd/_auth.scss) is the signal.
  const handleAutofill = (event: React.AnimationEvent<HTMLInputElement>) => {
    if (event.animationName === 'ddAutofillStart' && step === 'email') setStep('password');
  };

  const sendMagicLink = async () => {
    if (!isValidEmail(email)) {
      setStep('email');
      setEmailError(EMAIL_ERROR);
      return;
    }
    setMagicError(null);
    setMagicState('sending');
    const result = await requestMagicLink({
      email: email.trim(),
      purpose,
      returnPath: returnPath || undefined,
      captchaToken: captchaToken || undefined,
      csrfToken: csrf,
    });
    if (captchaSiteKey) {
      setCaptchaToken(null);
      setCaptchaResetKey((key) => key + 1);
    }
    if (result.ok) {
      setMagicState('sent');
      return;
    }
    setMagicState('idle');
    setMagicError(result.ok === false && result.reason === 'captcha' ? CAPTCHA_ERROR : NETWORK_ERROR);
  };

  // A captcha token is single use, so a resend goes back through the widget.
  const resend = async () => {
    if (captchaSiteKey) {
      setMagicState('idle');
      return;
    }
    await requestMagicLink({ email: email.trim(), purpose, returnPath: returnPath || undefined, csrfToken: csrf });
  };

  if (magicState === 'sent') {
    return (
      <div className="dd-two-step">
        <MagicLinkSent
          email={email}
          onResend={resend}
          onUsePassword={() => {
            setMagicState('idle');
            setStep('password');
          }}
        />
      </div>
    );
  }

  return (
    <form
      className="dd-form dd-two-step"
      action={sessionPath}
      method="post"
      onSubmit={handleSubmit}
      noValidate={step === 'email'}
      data-testid="two-step-signin"
    >
      <input type="hidden" name="authenticity_token" value={csrf} />
      {invitationRedirectPath ? (
        <input type="hidden" name="user[invitation_redirect_path]" value={invitationRedirectPath} />
      ) : null}

      <div className="dd-form__field">
        <label htmlFor="user_email">Email Address</label>
        <input
          id="user_email"
          name="user[email]"
          type="email"
          className="dd-input"
          autoComplete="email"
          placeholder="Email Address"
          value={email}
          readOnly={emailLocked}
          aria-readonly={emailLocked || undefined}
          autoFocus={step === 'email'}
          required
          ref={emailRef}
          onChange={(event) => {
            setEmail(event.target.value);
            if (emailError) setEmailError(null);
          }}
        />
        {emailError ? (
          <p className="dd-two-step__error" role="alert">
            {emailError}
          </p>
        ) : null}
        {open && !emailLocked ? (
          <button
            type="button"
            className="dd-auth__link dd-two-step__change"
            data-testid="signin-change-email"
            onClick={changeEmail}
          >
            Change
          </button>
        ) : null}
      </div>

      {step === 'email' ? (
        <button type="submit" className="dd-auth__cta" data-testid="signin-continue">
          Continue
        </button>
      ) : null}

      <div className={`dd-two-step__reveal${open ? ' is-open' : ''}`} aria-hidden={!open}>
        <div className="dd-form__field">
          <label htmlFor="user_password">Password</label>
          <input
            id="user_password"
            name="user[password]"
            type="password"
            className="dd-input"
            autoComplete="current-password"
            placeholder="Password"
            ref={passwordRef}
            value={password}
            required={open}
            tabIndex={hiddenTab}
            onChange={(event) => handlePasswordChange(event.target.value)}
            onAnimationStart={handleAutofill}
          />
        </div>

        <div className="dd-auth__meta">
          {showRememberMe ? (
            <label className="dd-form__check" htmlFor="rememberme">
              {/* Top-level `rememberme` on purpose; see users/sessions/new.html.erb. */}
              <input type="checkbox" id="rememberme" name="rememberme" value="forever" tabIndex={hiddenTab} />
              Remember me
            </label>
          ) : (
            <span />
          )}
          <a href={forgotPasswordUrl} className="dd-auth__link" tabIndex={hiddenTab}>
            Forgot password?
          </a>
        </div>

        <button type="submit" className="dd-auth__cta" data-testid="signin-password" tabIndex={hiddenTab}>
          Sign in with password
        </button>

        {captchaSiteKey && open ? (
          <TurnstileWidget siteKey={captchaSiteKey} onToken={setCaptchaToken} resetKey={captchaResetKey} />
        ) : null}
        {magicError ? (
          <p className="dd-two-step__error" role="alert">
            {magicError}
          </p>
        ) : null}
        <button
          type="button"
          className="dd-auth__cta dd-auth__cta--magic"
          data-testid="signin-magic-link"
          tabIndex={hiddenTab}
          onClick={sendMagicLink}
          disabled={magicState === 'sending' || (Boolean(captchaSiteKey) && !captchaToken)}
        >
          {magicState === 'sending' ? 'Sending link...' : 'Sign in with magic link'}
        </button>
      </div>
    </form>
  );
};

export default TwoStepSignIn;
